import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button, message, Space, Typography } from 'antd';
import {
  createPremiumPaymentIntent,
  confirmPremiumPurchase
} from '../../services/PaymentServiceAnnuaire';

const { Text } = Typography;

interface PaymentFormPremiumProps {
  amount: number;
  professionId: number;
  userId: number;
  professionName: string;
  onSuccess: (position: number) => void;
  onCancel: () => void;
}

const PaymentFormPremium: React.FC<PaymentFormPremiumProps> = ({
  amount,
  professionId,
  userId,
  professionName,
  onSuccess,
  onCancel
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      message.error("Stripe n'est pas encore initialisé");
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      message.error("Erreur avec le formulaire de carte");
      return;
    }

    setLoading(true);

    try {
      // 1️⃣ Créer le PaymentIntent
      console.log("💰 Étape 1: Création du PaymentIntent");
      const { clientSecret } = await createPremiumPaymentIntent(
        amount,
        professionId,
        userId
      );

      // 2️⃣ Confirmer le paiement
      console.log("💳 Étape 2: Confirmation du paiement");
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        }
      });

      if (result.error) {
        console.error("❌ Erreur paiement:", result.error);
        message.error(result.error.message || "Le paiement a échoué");
        setLoading(false);
        return;
      }

      // 3️⃣ Paiement réussi → Confirmer l'achat premium
      if (result.paymentIntent?.status === 'succeeded') {
        console.log("✅ Étape 3: Paiement réussi, confirmation de l'achat");
        
        const confirmation = await confirmPremiumPurchase({
          paymentIntentId: result.paymentIntent.id,
          professionId,
          userId
        });

        console.log("🎉 Achat premium confirmé!", confirmation);
        message.success(`Félicitations ! Vous êtes position #${confirmation.position}`);
        onSuccess(confirmation.position);
      } else {
        message.error("Le paiement n'a pas été confirmé");
      }

    } catch (error: any) {
      console.error("❌ Erreur lors du paiement:", error);
      message.error(error.message || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

// Style personnalisé pour CardElement - VERSION CORRIGÉE
const cardStyle = {
  style: {
    base: {
      color: '#2d3e50',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#6c757d'
      },
      // ❌ SUPPRIMEZ le padding d'ici - il ne fonctionne pas
    },
    invalid: {
      color: '#faad14',
      iconColor: '#faad14'
    }
  },
  hidePostalCode: true
};

return (
  <form onSubmit={handleSubmit}>
    <div style={{ marginBottom: 24 }}>
      <Text strong style={{ display: 'block', marginBottom: 8, color: '#2d3e50' }}>
        Informations de paiement
      </Text>
      <div style={{
        border: 'none',  
        borderRadius: 8,
        backgroundColor: 'transparent',  
      }}>
        <CardElement 
          options={cardStyle} 
          className="StripeElement" 
        />
      </div>
      <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
        Paiement sécurisé par Stripe
      </Text>
    </div>


      <div style={{
        backgroundColor: '#fff4e6',
        padding: 16,
        borderRadius: 8,
        marginBottom: 24
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text style={{ color: '#6c757d', fontSize: 14 }}>Profession</Text>
            <Text strong style={{ display: 'block', color: '#2d3e50' }}>
              {professionName}
            </Text>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Text style={{ color: '#6c757d', fontSize: 14 }}>Total à payer</Text>
            <Text strong style={{ fontSize: 24, color: '#f4a261' }}>
              {amount} €
            </Text>
          </div>
        </div>
      </div>

      <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
        <Button onClick={onCancel} disabled={loading}>
          Annuler
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          disabled={!stripe || loading}
          loading={loading}
          style={{
            backgroundColor: '#f4a261',
            borderColor: '#f4a261',
            minWidth: 120
          }}
        >
          {loading ? 'Paiement en cours...' : `Payer ${amount} €`}
        </Button>
      </Space>
    </form>
  );
};

export default PaymentFormPremium;